import { useMemo, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ColDef, IDetailCellRendererParams } from "@ag-grid-community/core";
import apiClient from "../../../api/apiClient";
import { shortDateString } from "../../../utils/dateFormatters";
import { formatCurrency } from "../../../utils/numberFormatters";
import { MgrBrandDealDataRow, MgrBrandDealDataRowAdapter } from "./MgrBrandDealDataRow";

const MgrBrandDealAnalytics = () => {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const [rowData, setRowData] = useState<MgrBrandDealDataRow[]>();

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      aggFunc: "count",
      minWidth: 200,
      suppressAutoSize: false,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    };
  }, []);

  const detailCellRendererParams = useMemo<any>(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          {
            field: "paymentType",
            headerName: "Payment Type",
            filter: true,
            enableRowGroup: true,
          },
          {
            field: "cashPayment",
            headerName: "Cash Payment",
            valueFormatter: (p: any) =>
              p.value ? formatCurrency(p.value) : "-",
            minWidth: 150,
          },
          {
            field: "cashPaymentDue",
            headerName: "Payment Due Date",
            valueFormatter: (p: any) =>
              p.value ? shortDateString(p.value) : "-",
          },
          {
            field: "cashDeposit",
            headerName: "Deposit Amount",
            valueFormatter: (p: any) =>
              p.value ? formatCurrency(p.value) : "-",
            minWidth: 150,
          },
          {
            field: "cashDepositDate",
            headerName: "Deposit Date",
            valueFormatter: (p: any) =>
              p.value ? shortDateString(p.value) : "-",
          },
          {
            field: "equityComp",
            headerName: "Equity Details",
            hide: true,
          },
          {
            field: "equityType",
            headerName: "Equity Type",
            hide: true,
          },
          {
            field: "vestingSummary",
            headerName: "Vesting Schedule",
            wrapText: true,
            autoHeight: true,
            hide: true,
          },
          {
            field: "vestedDate",
            headerName: "Vesting Date",
            valueFormatter: (p: any) =>
              p.value ? shortDateString(p.value) : "-",
            hide: true,
          },
          {
            field: "performanceThreshhold",
            headerName: "Performance Target",
            hide: true,
          },
          {
            field: "performanceCompensation",
            headerName: "Performance Compensation",
            hide: true,
          },
        ],
        groupDefaultExpanded: 1,
        rowGroupPanelShow: "always",
      },
      getDetailRowData: (params: any) => {
        params.successCallback(params.data.children);
      },
    } as IDetailCellRendererParams;
  }, []);

  const [columnDefs, _] = useState<ColDef[]>([
    {
      field: "effectiveDate",
      headerName: "Start Date",
      sort: "desc",
      valueFormatter: (p: any) => shortDateString(p.value),
      cellRenderer: "agGroupCellRenderer",
    },
    { field: "talent", headerName: "Talent" },
    { field: "talentAgency", headerName: "Talent Agency" },
    { field: "brand", headerName: "Brand" },
    { field: "brandAgency", headerName: "Brand Agency" },
    {
      field: "paymentTotal",
      headerName: "Cash Total",
      valueFormatter: (p: any) => formatCurrency(p.value),
    },
    {
      field: "term",
      headerName: "Term",
      wrapText: true,
      autoHeight: true,
      maxWidth: 250,
      minWidth: 250,
      cellStyle: {
        lineHeight: "normal",
      },
      filter: false,
      enableRowGroup: false,
    },
    {
      field: "terminationDate",
      headerName: "End Date",
      valueFormatter: (p: any) => shortDateString(p.value),
    },
    { field: "dealType", headerName: "Deal Type" },
    {
      field: "paymentSummary",
      headerName: "Payment Summary",
      wrapText: true,
      autoHeight: true,
      maxWidth: 500,
      minWidth: 500,
      cellStyle: {
        lineHeight: "normal",
      },
      filter: false,
      enableRowGroup: false,
    },
    {
      field: "deliverablesSummary",
      headerName: "Deliverables",
      wrapText: true,
      autoHeight: true,
      maxWidth: 500,
      minWidth: 500,
      cellStyle: {
        lineHeight: "normal",
      },
      filter: false,
      enableRowGroup: false,
    },
    {
      field: "exclusivitySummary",
      headerName: "Exclusivity",
      wrapText: true,
      autoHeight: true,
      maxWidth: 250,
      minWidth: 250,
      cellStyle: {
        lineHeight: "normal",
      },
      filter: false,
      enableRowGroup: false,
    },
    {
      field: "hasAutoRenew",
      headerName: "Auto Renewal",
      valueParser: (p: any) => (p.value === true ? "Yes" : "No"),
      valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
    },
    {
      field: "hasFtcRisk",
      headerName: "FTC Risk",
      valueParser: (p: any) => (p.value === true ? "Yes" : "No"),
      valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
    },
    {
      field: "hasIndemnification",
      headerName: "Indemnification",
      valueParser: (p: any) => (p.value === true ? "Yes" : "No"),
      valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
    },
    {
      field: "hasIpAssignment",
      headerName: "IP Assignment",
      valueParser: (p: any) => (p.value === true ? "Yes" : "No"),
      valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
    },
    {
      field: "hasConfidentiality",
      headerName: "Confidentiality",
      valueParser: (p: any) => (p.value === true ? "Yes" : "No"),
      valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
    },
  ]);

 const getData = async () => {
    try {
      const deals = await apiClient
        .get("/multi-team/brand-deal", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        })
        .then((resp) => resp.data);
      setRowData(new MgrBrandDealDataRowAdapter().toContracts(deals));
    } catch (error) {
      setRowData([]);
      throw error;
    }
  };

  const cellSelection = useMemo(() => {
    return true;
  }, []);

  useEffect(() => {
    getData();
  }, []);

  return (
    <div style={containerStyle}>
      <div style={gridStyle} className="ag-theme-quartz">
        <AgGridReact<MgrBrandDealDataRow>
          rowData={rowData}
          masterDetail={true}
          isRowMaster={(row) => row.children && row.children.length > 0}
          columnDefs={columnDefs}
          sideBar={{ toolPanels: ["columns", "filters"] }}
          rowGroupPanelShow={"always"}
          enableCharts={true}
          cellSelection={cellSelection}
          defaultColDef={defaultColDef}
          detailCellRendererParams={detailCellRendererParams}
        />
      </div>
    </div>
  );
};

export default MgrBrandDealAnalytics;
